import { useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { productsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { ImportResponse } from '../../api/types.js';
import { Modal } from '../../components/Modal.js';
import { Banner } from '../../components/Banner.js';

export interface ImportModalProps {
  onClose(): void;
  onImported(): void;
}

type Step = 'select' | 'preview' | 'done';

export function ImportModal({ onClose, onImported }: ImportModalProps) {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>('select');
  const [preview, setPreview] = useState<ImportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runDryRun() {
    if (!file) {
      setError(t('products.import.selectFileFirst'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await productsApi.importCsv(file, true);
      setPreview(result);
      setStep('preview');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmImport() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await productsApi.importCsv(file, false);
      setPreview(result);
      setStep('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  const hasErrors = (preview?.errorCount ?? 0) > 0;

  return (
    <Modal title={t('products.import.title')} onClose={onClose} wide>
      <p className="field-hint">{t('products.import.help')}</p>
      {error && <Banner kind="danger">{error}</Banner>}

      {step === 'select' && (
        <>
          <label htmlFor="import-file">
            {t('products.import.selectFile')}
            <input
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <div className="modal__actions">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void runDryRun()}>
              {t('products.import.dryRunButton')}
            </button>
          </div>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <h3>{t('products.import.previewTitle')}</h3>
          {hasErrors && <Banner kind="warn">{t('products.import.hasErrorsWarning')}</Banner>}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('products.import.row')}</th>
                  <th>{t('products.sku')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('products.import.errors')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.results.map((row) => (
                  <tr key={row.row}>
                    <td className="numeric">{row.row}</td>
                    <td className="numeric">{row.sku}</td>
                    <td>
                      <span
                        className={`badge ${
                          row.errors.length > 0
                            ? 'badge--danger'
                            : row.action === 'skip'
                              ? 'badge--neutral'
                              : 'badge--ok'
                        }`}
                      >
                        {row.errors.length > 0
                          ? t('products.import.errors')
                          : row.action === 'create'
                            ? t('products.import.willCreate')
                            : row.action === 'update'
                              ? t('products.import.willUpdate')
                              : t('products.import.willSkip')}
                      </span>
                    </td>
                    <td>{row.errors.length > 0 ? row.errors.join('; ') : t('products.import.noErrors')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="modal__actions">
            <button type="button" className="btn btn--secondary" onClick={() => setStep('select')}>
              {t('common.back')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => void confirmImport()}
            >
              {busy ? t('products.import.confirming') : t('products.import.confirmButton')}
            </button>
          </div>
        </>
      )}

      {step === 'done' && preview && (
        <>
          <Banner kind="ok">{t('products.import.success', { count: preview.appliedCount })}</Banner>
          <div className="modal__actions">
            <button type="button" className="btn btn--primary" onClick={onImported}>
              {t('common.close')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
